<template>
  <div>
    <div v-if="!hasAnyContent" class="text-888" style="padding:10px;background:#fafbfc;border:1px solid #eee;border-radius:4px;">
      입력된 내용이 없습니다. (양식이 비어 있거나 모든 입력이 기본값입니다.)
    </div>

    <table v-if="(data.workPeriod && data.workPeriod.length) || transportMethodsText" class="tbl v-tbl">
      <colgroup><col width="180px"><col></colgroup>
      <tbody>
        <tr v-if="data.workPeriod && data.workPeriod.length"><th>작업기간</th><td>{{ formatRange(data.workPeriod) }}</td></tr>
        <tr v-if="transportMethodsText"><th>중량물 운반방법</th><td>{{ transportMethodsText }}</td></tr>
      </tbody>
    </table>

    <!-- 운전원 -->
    <template v-if="filledOperators.length">
      <div class="sub-title mt-15">운전원</div>
      <table class="tbl">
        <thead><tr><th>성명</th><th>면허</th><th>연락처</th></tr></thead>
        <tbody>
          <tr v-for="(op, oi) in filledOperators" :key="'op-'+oi">
            <td>{{ op.name || '-' }}</td><td>{{ op.license || '-' }}</td><td>{{ op.phone || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </template>

    <!-- 유도자 -->
    <template v-if="filledGuides.length">
      <div class="sub-title mt-15">유도자</div>
      <table class="tbl">
        <thead><tr><th>성명</th><th>신호방법</th><th>연락처</th></tr></thead>
        <tbody>
          <tr v-for="(g, gi) in filledGuides" :key="'gu-'+gi">
            <td>{{ g.name || '-' }}</td>
            <td>{{ (g.signalMethods && g.signalMethods.length) ? g.signalMethods.join(', ') : '-' }}</td>
            <td>{{ g.phone || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </template>

    <!-- 기계·장비 제원 -->
    <template v-if="hasEquipmentSection && filledEquipment.length">
      <div class="sub-title mt-15">기계·장비 제원</div>
      <div v-for="(eq, ei) in filledEquipment" :key="'eq-'+ei" class="equip-card">
        <div class="equip-card-title">장비 {{ ei + 1 }}<span v-if="eq.equipType" class="ml-10 text-888">({{ eq.equipType }})</span></div>
        <table class="tbl v-tbl">
          <colgroup><col width="160px"><col><col width="160px"><col></colgroup>
          <tbody>
            <tr v-for="(pair, pi) in pairRows(equipmentRows(eq))" :key="'eqr-'+ei+'-'+pi">
              <th>{{ pair[0][0] }}</th>
              <td v-if="pair[1]">{{ pair[0][1] }}</td>
              <td v-else colspan="3">{{ pair[0][1] }}</td>
              <template v-if="pair[1]">
                <th>{{ pair[1][0] }}</th>
                <td>{{ pair[1][1] }}</td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- 줄걸이 -->
    <template v-if="hasLifting && filledLifting.length">
      <div class="sub-title mt-15">줄걸이 용구</div>
      <div v-for="(lift, li) in filledLifting" :key="'lf-'+li" class="equip-card">
        <div class="equip-card-title">줄걸이 {{ li + 1 }}</div>
        <table class="tbl v-tbl">
          <colgroup><col width="160px"><col><col width="160px"><col></colgroup>
          <tbody>
            <tr v-for="(pair, pi) in pairRows(liftingRows(lift))" :key="'lfr-'+li+'-'+pi">
              <th>{{ pair[0][0] }}</th>
              <td v-if="pair[1]">{{ pair[0][1] }}</td>
              <td v-else colspan="3">{{ pair[0][1] }}</td>
              <template v-if="pair[1]">
                <th>{{ pair[1][0] }}</th>
                <td>{{ pair[1][1] }}</td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- 중량물 제원 -->
    <template v-if="hasHeavyCargo && filledCargo.length">
      <div class="sub-title mt-15">중량물 제원</div>
      <table class="tbl">
        <thead>
          <tr>
            <th>품명</th><th>단위중량</th><th>종류</th><th>고정방법</th><th>형상</th><th>총중량</th>
            <th>수직</th><th>수평</th><th>작업반경</th><th>총양중</th><th>1회양중수량</th><th>1회양중중량</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(c, ci) in filledCargo" :key="'cg-'+ci">
            <td>{{ c.name || '-' }}</td><td>{{ c.unitWeight || '-' }}</td>
            <td>{{ c.type || '-' }}</td><td>{{ c.fixMethod || '-' }}</td>
            <td>{{ c.shape || '-' }}</td><td>{{ c.totalWeight || '-' }}</td>
            <td>{{ c.verticalDistance || '-' }}</td><td>{{ c.horizontalDistance || '-' }}</td>
            <td>{{ c.workRadius || '-' }}</td><td>{{ c.totalQty || '-' }}</td>
            <td>{{ c.qtyPerLift || '-' }}</td><td>{{ c.weightPerLift || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </template>

    <!-- 지게차 화물·적재 -->
    <template v-if="permKey === 'forkliftPermit' && data.cargoAndLoad">
      <div class="sub-title mt-15">화물 및 적재 상태</div>
      <table class="tbl v-tbl">
        <colgroup><col width="180px"><col></colgroup>
        <tbody>
          <tr><th>지게차 허용하중</th><td>{{ data.cargoAndLoad.forkliftCapacity || '-' }}</td></tr>
          <tr v-if="data.cargoAndLoad.cargo"><th>화물 품명</th><td>{{ data.cargoAndLoad.cargo.name || '-' }} / 단위중량 {{ data.cargoAndLoad.cargo.unitWeight || '-' }} / 종류·형상 {{ data.cargoAndLoad.cargo.type || '-' }} · {{ data.cargoAndLoad.cargo.shape || '-' }} / 크기 {{ data.cargoAndLoad.cargo.size || '-' }}</td></tr>
          <tr v-if="data.cargoAndLoad.cargo"><th>운반</th><td>1회 {{ data.cargoAndLoad.cargo.qtyPerTrip || '-' }} / 중량 {{ data.cargoAndLoad.cargo.weightPerTrip || '-' }} / 총 {{ data.cargoAndLoad.cargo.totalQty || '-' }} (총중량 {{ data.cargoAndLoad.cargo.totalWeight || '-' }})</td></tr>
          <tr v-if="data.cargoAndLoad.loading"><th>적재상태</th><td>높이 {{ data.cargoAndLoad.loading.stackHeight || '-' }} / 너비 {{ data.cargoAndLoad.loading.stackWidth || '-' }} · 통로 높이 {{ data.cargoAndLoad.loading.minPathHeight || '-' }} / 너비 {{ data.cargoAndLoad.loading.minPathWidth || '-' }} · 시야 {{ data.cargoAndLoad.loading.driverVisibility || '-' }} / 반경 {{ data.cargoAndLoad.loading.requiredRadius || '-' }}</td></tr>
        </tbody>
      </table>
    </template>

    <!-- 작업장소 사전조사 -->
    <template v-if="siteRows.length">
      <div class="sub-title mt-15">작업장소 사전 조사</div>
      <table class="tbl v-tbl">
        <colgroup><col width="220px"><col></colgroup>
        <tbody>
          <tr v-for="(r, ri) in siteRows" :key="'si-'+ri"><th>{{ r[0] }}</th><td>{{ r[1] }}</td></tr>
        </tbody>
      </table>
    </template>

    <!-- 작업순서 -->
    <template v-if="filledProcedures.length">
      <div class="sub-title mt-15">작업순서 및 안전 작업 방법</div>
      <permit-procedure-table :procedures="filledProcedures" />
    </template>

    <!-- 첨부서류 -->
    <template v-if="attachmentDocList.length">
      <div class="sub-title mt-15">첨부서류</div>
      <table class="tbl v-tbl">
        <colgroup><col width="280px"><col></colgroup>
        <tbody>
          <tr v-for="(r, ri) in attachmentDocList" :key="'at-'+ri">
            <th>{{ r.label }}</th>
            <td>
              <template v-if="r.files.length">
                <span v-for="(f, fi) in r.files" :key="'atf-'+ri+'-'+fi" style="margin-right:12px;">
                  <a v-if="f.id" href="javascript:void(0)" class="under-line" @click="downloadFile(f)">{{ f.name }}</a>
                  <span v-else>{{ f.name }}</span>
                </span>
              </template>
              <span v-else class="text-888">체크됨</span>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import PermitProcedureTable from './PermitProcedureTable.vue';

export default {
  name: 'HeavyPermitSummary',
  components: { PermitProcedureTable },
  props: {
    data: { type: Object, required: true },
    permKey: { type: String, default: '' }
  },
  computed: {
    isHeavyLift() {
      return this.permKey === 'heavyLiftPermit';
    },
    filledOperators() {
      return (this.data.operators || []).filter((o) => o && (o.name || o.license || o.phone));
    },
    filledGuides() {
      return (this.data.guides || []).filter((g) => g && (g.name || g.phone || (g.signalMethods && g.signalMethods.length)));
    },
    filledEquipment() {
      return (this.data.equipmentList || []).filter((eq) => {
        if (!eq) return false;
        const keys = ['modelName','machineName','vehicleName','manufacturer','registrationNo','vehicleRegNo','machineSpec','equipWeight','capacity','ratedLoad','maxRatedLoad','maxWorkHeight','platformSpec','craneType','equipType','otherSpec','workRadius','transport','modelYear','modelTypeYear','manufactureYear','manufactureDate','serialNo','maxLiftHeight'];
        return keys.some((k) => eq[k]);
      });
    },
    filledLifting() {
      return (this.data.liftingList || []).filter((l) => {
        if (!l) return false;
        return l.liftFeasible || l.diameter || l.safeWorkLoad || l.riggingCount
          || (l.riggingTools && l.riggingTools.length) || (l.auxiliaryTools && l.auxiliaryTools.length)
          || l.maxWorkLoad || l.loadFactor || l.safetyFactor;
      });
    },
    filledCargo() {
      return (this.data.cargoList || []).filter((c) => {
        if (!c) return false;
        return c.name || c.unitWeight || c.type || c.totalWeight || c.workRadius || c.totalQty || c.qtyPerLift || c.weightPerLift;
      });
    },
    filledProcedures() {
      return (this.data.procedures || []).filter((p) => p && (p.item || p.procedure || p.hazard || p.currentMeasure || p.riskLevel || p.additionalMeasure));
    },
    hasLifting() {
      if (this.isHeavyLift) {
        return ((this.data.transportMethods || []).indexOf('줄걸이 용구 등 사용') >= 0);
      }
      return !!(this.data && (this.data.hasLifting || this.data.hasRigging));
    },
    hasHeavyCargo() {
      if (this.isHeavyLift) {
        return (this.data.transportMethods || []).length > 0;
      }
      return !!(this.data && this.data.hasHeavyCargo);
    },
    hasEquipmentSection() {
      if (this.isHeavyLift) {
        return ((this.data.transportMethods || []).indexOf('기계·장비 사용') >= 0);
      }
      return this.filledEquipment.length > 0;
    },
    transportMethodsText() {
      const arr = this.data && this.data.transportMethods;
      return (arr && arr.length) ? arr.join(', ') : '';
    },
    attachmentDocList() {
      const arr = this.data && this.data.attachmentDocs;
      if (!arr || !arr.length) return [];
      // 체크됐거나 파일이 하나라도 있는 문서만. 파일은 {id, name} 형태 → id 로 다운로드 링크 생성.
      return arr
        .filter((d) => d && (d.checked || (d.files && d.files.length)))
        .map((d) => ({
          label: d.label,
          files: (d.files || []).filter((f) => f && (f.id || f.name))
        }));
    },
    siteRows() {
      const s = this.data && this.data.siteInvestigation;
      if (!s) return [];
      const rows = [];
      const push = (label, val) => {
        if (val === undefined || val === null || val === '') return;
        if (Array.isArray(val) && !val.length) return;
        rows.push([label, val]);
      };
      if (s.underground && s.underground.length) {
        const hasFilled = s.underground.some((u) => u.applicable !== null || u.measure);
        if (hasFilled) {
          push('지하매설물 조사', s.underground.map((u) => `${u.name}: ${u.applicable === true ? '해당' : (u.applicable === false ? '해당없음' : '-')}${u.measure ? ' / ' + u.measure : ''}`).join(' | '));
        }
      }
      const groundTypeText = [
        s.groundTypeCheck ? `지반종류(${s.groundTypeText || ''})` : null,
        s.slopeCheck ? `경사(${s.slopeText || ''})` : null,
        (s.conditions && s.conditions.length) ? `상태: ${s.conditions.join(', ')}` : null,
        s.specialNoteCheck ? `특이사항(${s.specialNoteText || ''})` : null
      ].filter(Boolean).join(' / ');
      if (groundTypeText) push('지형 및 지반상태', groundTypeText);
      if (s.groundTypes && s.groundTypes.length) push('지반 종류', s.groundTypes.join(', ') + (s.groundTypeOther && s.groundTypeOtherText ? ', 기타: ' + s.groundTypeOtherText : ''));
      if (s.groundwaterStatus) push('지하수위 등 함수상태', s.groundwaterStatus + (s.groundwaterMeasure ? ' / ' + s.groundwaterMeasure : ''));
      push('필요 지내력', s.requiredLoadCapacity);
      push('작업장소 지내력', s.actualLoadCapacity);
      if (s.reinforcementMethods && s.reinforcementMethods.length) push('지반 보강방법', s.reinforcementMethods.join(', ') + (s.reinforcementOther && s.reinforcementOtherText ? ', 기타: ' + s.reinforcementOtherText : ''));
      if (s.accessRestrictions && s.accessRestrictions.length) push('작업반경 내 출입금지 조치', s.accessRestrictions.join(', ') + (s.accessRestrictionOther && s.accessRestrictionOtherText ? ', 기타: ' + s.accessRestrictionOtherText : ''));
      if (s.nearbyPowerLines) push('주변 가공전선 접촉 가능 여부', s.nearbyPowerLines + (s.nearbyPowerLinesMeasure ? ' / ' + s.nearbyPowerLinesMeasure : ''));
      if (s.nearbyObstacle) push('주변 지장물 간섭 여부', s.nearbyObstacle + (s.nearbyObstacleMeasure ? ' / ' + s.nearbyObstacleMeasure : ''));
      if (s.workerEntryCheck) push('작업자 출입구/이동통로 구분', s.workerEntryCheck + (s.workerEntryMeasure ? ' / ' + s.workerEntryMeasure : ''));
      if (s.roadCollapseCheck) push('도로/갓길/경사면 추락위험', s.roadCollapseCheck + (s.roadCollapseMeasure ? ' / ' + s.roadCollapseMeasure : ''));
      if (s.outriggerConditions && s.outriggerConditions.length) push('아웃트리거 설치장소', s.outriggerConditions.join(', '));
      if (s.outriggerReinforcements && s.outriggerReinforcements.length) push('아웃트리거 보강방법', s.outriggerReinforcements.join(', ') + (s.outriggerReinforcementOther && s.outriggerReinforcementOtherText ? ', 기타: ' + s.outriggerReinforcementOtherText : ''));
      if (s.obstacleCheck) push('작업대 주변 지장물 간섭', s.obstacleCheck + (s.obstacleMeasure ? ' / ' + s.obstacleMeasure : ''));
      return rows;
    },
    hasAnyContent() {
      return this.filledOperators.length > 0
          || this.filledGuides.length > 0
          || (this.hasEquipmentSection && this.filledEquipment.length > 0)
          || (this.hasLifting && this.filledLifting.length > 0)
          || (this.hasHeavyCargo && this.filledCargo.length > 0)
          || this.filledProcedures.length > 0
          || this.siteRows.length > 0
          || this.attachmentDocList.length > 0
          || !!this.transportMethodsText
          || (this.data.workPeriod && this.data.workPeriod.length > 0);
    }
  },
  methods: {
    downloadFile(f) {
      if (!f || !f.id) return;
      // 외부 작성자(비 SSO) 최종확인 진입은 SSO 예외 별칭 경로로 내려받는다.
      const base = this.$route && this.$route.query && this.$route.query.flow === 'apply'
        ? '/api/public/work-permit/view' : '/api/private/work-permit';
      window.open(base + '/file/' + encodeURIComponent(f.id) + '/download', '_blank');
    },
    formatRange(range) {
      if (!range || !range.length) return '-';
      const f = range[0] ? moment(range[0]).format('YYYY-MM-DD') : '';
      const t = range[1] ? moment(range[1]).format('YYYY-MM-DD') : '';
      return f && t ? `${f} ~ ${t}` : (f || t || '-');
    },
    pairRows(rows) {
      const out = [];
      for (let i = 0; i < rows.length; i += 2) {
        out.push([rows[i], rows[i + 1] || null]);
      }
      return out;
    },
    equipmentRows(eq) {
      const rows = [];
      const push = (label, val) => {
        if (val === undefined || val === null || val === '') return;
        rows.push([label, val]);
      };
      push('모델명', eq.modelName);
      push('건설기계명', eq.machineName);
      push('차명', eq.vehicleName);
      push('제조사', eq.manufacturer);
      push('등록번호', eq.registrationNo || eq.vehicleRegNo);
      push('차종', eq.vehicleType);
      push('형식·연도', eq.modelYear || eq.modelTypeYear);
      push('제조 연도', eq.manufactureYear);
      push('제조 연월', eq.manufactureDate);
      push('일련번호', eq.serialNo);
      push('기계 규격', eq.machineSpec);
      push('구동형식', eq.driveType);
      push('규격(작업능력)', eq.capacity);
      push('작업가능반경', eq.workRadius);
      push('운반거리', eq.transport);
      push('장비중량', eq.equipWeight);
      push('최고속도', eq.maxSpeed);
      push('제한속도', eq.speedLimit);
      push('차량총중량', eq.vehicleGrossWeight);
      push('등판능력', eq.climbingAbility);
      push('적재함 용량', eq.loadCapacity);
      push('적재함 크기', eq.loadSize);
      push('덤프 최대경사각', eq.dumpMaxTiltAngle);
      push('덤프 상승시간', eq.dumpLiftTime);
      push('마스트 경사각', eq.mastAngle);
      push('마스트 높이', eq.mastHeight);
      push('선회반경', eq.turningRadius);
      push('정격하중', eq.ratedLoad);
      push('최대정격', eq.maxRatedLoad);
      push('최대인양높이', eq.maxLiftHeight);
      push('최대 작업높이', eq.maxWorkHeight);
      push('최대 작업반경', eq.maxWorkRadius);
      push('작업대 규격', eq.platformSpec);
      push('허용탑승인원', eq.maxOccupancy);
      push('최대허용풍속', eq.maxWindSpeed);
      push('최대허용경사', eq.maxSlope);
      if (eq.workDevices && eq.workDevices.length) {
        push('작업장치', eq.workDevices.join(', ') + (eq.workDeviceOther && eq.workDeviceOtherText ? ', 기타: ' + eq.workDeviceOtherText : ''));
      }
      push('붐·암 최대높이', eq.boomMaxHeight);
      push('붐·암 길이', eq.boomLength);
      push('표준붐 작업반경', eq.standardBoomRadius);
      push('붐길이(최대)', eq.boomLengthMax);
      push('붐 최대각도', eq.boomMaxAngle);
      push('붐 최소각도', eq.boomMinAngle);
      push('최소정격(하중)', eq.minRatedLoad);
      push('훅 최대 지상높이', eq.hookMaxHeight);
      push('아웃트리거 최대 폭', eq.outriggerWidth);
      push('아웃트리거 설치폭', eq.outriggerSize);
      push('크레인 종류', eq.craneType);
      push('검사기관', eq.inspectionAgency);
      if (eq.operationMethods && eq.operationMethods.length) {
        push('조작방식', eq.operationMethods.join(', '));
      }
      push('구조변경일', eq.modificationDate);
      push('구조변경업체', eq.modificationCompany);
      push('기타제원', eq.otherSpec);
      if (eq.insuranceStatus) {
        let v = eq.insuranceStatus;
        if (eq.insuranceName) v += ' / ' + eq.insuranceName;
        if (eq.insuranceExpiry) v += ' (~' + eq.insuranceExpiry + ')';
        push('보험여부', v);
      }
      if (eq.regularInspectionDate || eq.inspectionExpiry) {
        let v = eq.regularInspectionDate || '-';
        if (eq.inspectionExpiry) v += ' ~ ' + eq.inspectionExpiry;
        push('정기검사', v);
      }
      if (eq.hasCertificate !== null && eq.hasCertificate !== undefined) {
        push('건설기계등록증', eq.hasCertificate ? '유' : '무');
      }
      return rows;
    },
    liftingRows(lift) {
      const rows = [];
      const push = (label, val) => {
        if (val === undefined || val === null || val === '') return;
        rows.push([label, val]);
      };
      push('인양능력 검토 결과', lift.liftFeasible);
      if (lift.riggingTools && lift.riggingTools.length) {
        push('줄걸이 용구', lift.riggingTools.join(', ') + (lift.riggingToolOther && lift.riggingToolOtherText ? ', 기타: ' + lift.riggingToolOtherText : ''));
      }
      if (lift.auxiliaryTools && lift.auxiliaryTools.length) {
        push('보조용구', lift.auxiliaryTools.join(', ') + (lift.auxiliaryToolOther && lift.auxiliaryToolOtherText ? ', 기타: ' + lift.auxiliaryToolOtherText : ''));
      }
      push('직경', lift.diameter);
      push('안전작업하중', lift.safeWorkLoad);
      push('줄걸이 수', lift.riggingCount);
      push('하중계수(장력)', lift.loadFactor);
      push('안전계수', lift.safetyFactor);
      push('최대사용하중', lift.maxWorkLoad);
      return rows;
    }
  }
};
</script>

<style scoped>
/* NDKC .tbl 기본 스타일 그대로 사용 — .summary-tbl 정의 제거 */
.sub-title {
  font-weight: 600;
  font-size: 13px;
  color: #333;
  border-bottom: 1px solid #e2e2e2;
  padding-bottom: 4px;
  margin-bottom: 8px;
}
.equip-card {
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #fafbfc;
}
.equip-card-title {
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}
</style>